// Objets posés d'une carte rangés par morceaux de 16 × 16 cases, triés une fois pour toutes dans l'ordre de dessin :
// à chaque image, le rendu ne regarde que les morceaux autour de l'écran au lieu de toute la carte (des milliers
// d'objets sur les grandes routes).
import { inDrawingOrder, motionOf, type MapElement, type MapObject } from './map-elements.ts';

const CHUNK = 16;
const chunkKey = (cx: number, cy: number) => cx * 65_536 + cy;

/** Débordement maximal des éléments au-delà de leurs cases, en cases (voir Scene.overflow). */
export interface ObjectOverflow {
  left: number;
  right: number;
  up: number;
  down: number;
}

export class ObjectGrid {
  readonly objects: readonly MapObject[];
  readonly elements: ReadonlyMap<number, MapElement>;
  /** Les objets (d'un élément connu) dans l'ordre de dessin : calque par calque, puis dans l'ordre où ils sont posés. */
  private readonly ordered: MapObject[];
  private readonly rank = new Map<MapObject, number>();
  /** Rangs des objets fixes dont les cases (débordements compris) touchent chaque morceau. */
  private readonly chunks = new Map<number, number[]>();
  /** Objets mobiles : ils peuvent être n'importe où, on les regarde toujours. */
  private readonly mobile: number[] = [];
  /** Marque des rangs déjà retenus par la dernière recherche (pour ne pas prendre deux fois un objet sur plusieurs morceaux). */
  private readonly seen: Uint32Array;
  private stamp = 0;

  constructor(objects: readonly MapObject[], elements: ReadonlyMap<number, MapElement>, overflow: ObjectOverflow) {
    this.objects = objects;
    this.elements = elements;
    this.ordered = inDrawingOrder(objects).filter((object) => elements.has(object.elementId));
    this.seen = new Uint32Array(this.ordered.length);
    this.ordered.forEach((object, rank) => {
      this.rank.set(object, rank);
      const element = elements.get(object.elementId)!;
      if (motionOf(object, element)) return void this.mobile.push(rank);
      const cx0 = Math.floor(Math.floor(object.x - overflow.left) / CHUNK), cx1 = Math.floor((Math.ceil(object.x + element.width + overflow.right) - 1) / CHUNK);
      const cy0 = Math.floor(Math.floor(object.y - overflow.up) / CHUNK), cy1 = Math.floor((Math.ceil(object.y + element.height + overflow.down) - 1) / CHUNK);
      for (let cy = cy0; cy <= cy1; cy++) {
        for (let cx = cx0; cx <= cx1; cx++) {
          const key = chunkKey(cx, cy);
          const list = this.chunks.get(key);
          if (list) list.push(rank);
          else this.chunks.set(key, [rank]);
        }
      }
    });
  }

  /**
   * Objets qui peuvent toucher les cases (x0, y0)–(x1, y1) à une case près, dans l'ordre de dessin : ceux des morceaux
   * concernés, les mobiles et `extra` (objets déplacés ailleurs, comme un rocher poussé). Le rendu garde ensuite ceux
   * qui sont vraiment visibles.
   */
  near(x0: number, y0: number, x1: number, y1: number, extra: Iterable<MapObject> = []): MapObject[] {
    const stamp = ++this.stamp;
    const ranks: number[] = [];
    const take = (rank: number | undefined) => {
      if (rank === undefined || this.seen[rank] === stamp) return;
      this.seen[rank] = stamp;
      ranks.push(rank);
    };
    for (let cy = Math.floor((y0 - 1) / CHUNK); cy <= Math.floor((y1 + 1) / CHUNK); cy++) {
      for (let cx = Math.floor((x0 - 1) / CHUNK); cx <= Math.floor((x1 + 1) / CHUNK); cx++) {
        for (const rank of this.chunks.get(chunkKey(cx, cy)) ?? []) take(rank);
      }
    }
    for (const rank of this.mobile) take(rank);
    for (const object of extra) take(this.rank.get(object));
    return ranks.sort((a, b) => a - b).map((rank) => this.ordered[rank]!);
  }
}
